import type { DocumentData } from 'firebase/firestore';
import type { FirestoreUser } from '../types';

/**
 * One `users/{uid}` document as the Users screen sees it.
 *
 * Pure, so the whitelist is testable without a Firestore listener. This mapper
 * IS a whitelist — a field omitted here is silently absent on every consumer,
 * which reads as a failed write rather than a missing mapping. `isTestUser` was
 * omitted from #570 onward: the admin's Save wrote it, this mapper dropped it,
 * so the checkbox re-opened unticked and could never be cleared.
 */
export function mapFirestoreUser(
  uid: string,
  data: DocumentData,
): FirestoreUser {
  return {
    uid,
    email: data.email,
    displayName: data.displayName,
    isAdmin: data.isAdmin === true || data.IsAdmin === true,
    isContributor: data.isContributor === true,
    plan: data.plan || 'free',
    createdAt: data.createdAt,
    lastLoginAt: data.lastLoginAt,
    disabled: data.disabled ?? false,
    accountStatus: data.accountStatus,
    deletionRequestedAt: data.deletionRequestedAt,
    deletionScheduledAt: data.deletionScheduledAt,
    submissionDisabled: data.submissionDisabled === true,
    // `=== true`, never left undefined: the editor diffs against this, so an
    // absent key must read as a definite `false` for unticking to write.
    isTestUser: data.isTestUser === true,
    lastAppVersion: data.lastAppVersion,
    lastAppVersionAt: data.lastAppVersionAt,
    // This mapper is a whitelist — a field omitted here is silently absent
    // on every consumer, which reads as a failed write rather than a
    // missing mapping. Both AI-limit fields have to be listed.
    aiUsage: data.aiUsage,
    aiRateLimitMode: data.aiRateLimitMode,
    // RevenueCat state. These already sit on every user doc — the webhook
    // has been writing them all along — so listing them here costs NOTHING:
    // the snapshot already carries the whole document. They were simply
    // being dropped on the floor by this whitelist.
    subscription: data.subscription,
    trialTier: data.trialTier,
    trialStartedAt: data.trialStartedAt,
    trialEndsAt: data.trialEndsAt,
    hasEverBeenPaid: data.hasEverBeenPaid === true,
    hasAiAddon: data.hasAiAddon === true,
    // Notification permission rides on the user doc, which this listener
    // already streams in full — so surfacing it costs NOTHING and needs no
    // callable. Left undefined when absent rather than defaulted: a user on
    // an older build has an UNKNOWN permission state, which is a different
    // claim from 'not_determined' (something we actually observed).
    notificationPermission: data.notificationPermission,
    notificationPermissionAt: data.notificationPermissionAt,
    // Launch diagnostics. All of these live on the user doc, which this
    // listener already streams in full — so the whole set costs NOTHING and
    // needs no callable. Every one is left undefined when absent: these are
    // app-release gated, and a defaulted 0 / {} would read as a measurement
    // rather than as "this user has not opened a build that reports it".
    notificationChannelsEnabled: data.notificationChannelsEnabled,
    lastAppOpenedAt: data.lastAppOpenedAt,
    installedAt: data.installedAt,
    launchCount:
      typeof data.launchCount === 'number' ? data.launchCount : undefined,
    osVersion: data.osVersion,
    deviceModel: data.deviceModel,
    locale: data.locale,
    timezone: data.timezone,
    brandMemberships: Array.isArray(data.brandMemberships)
      ? data.brandMemberships
      : undefined,
  } as FirestoreUser;
}
